// Speed up a construction job: pick one of the four speed-up consumables you own; one per job. Buying more is the Store's job.
import { useNavigate } from "react-router-dom";
import { Sheet } from "@/kit/overlays";
import { Button, Empty, Medallion, Row, RowText } from "@/kit/primitives";
import { Countdown } from "@/kit/city";
import { art } from "@/kit/art";
import { fmtNum } from "@/game/format";
import { fmtDuration } from "@/game/time";
import { owned, storefrontItems, type QueueEntry } from "@/game/city";
import { BUILDING_NAMES } from "@/game/constants";
import { api } from "@/api/client";
import { useCity } from "./CityRoute";

export function SpeedupSheet({ job, onClose }: { job: QueueEntry | null; onClose: () => void }) {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  if (!v) return null;
  const used = !!job?.item.has_used_speedup;
  // names and amounts from the server: the speed_up_construction storefront (the Store's speed_ups as a fallback)
  const fromServer = storefrontItems(v, "speed_up_construction");
  const speedups = (fromServer.length ? fromServer : storefrontItems(v, "speed_ups")).map((it) => ({ key: it.key, seconds: Number(it.value) || 0, label: it.name }));
  return (
    <Sheet open={!!job} onClose={onClose} label="Speed up" title={<h3 className="eyebrow" style={{ margin: "0 0 6px" }}>Speed up</h3>}>
      {job && (
        <>
          <p className="muted" style={{ margin: "0 0 14px", font: "400 var(--fs-body2)/1.4 var(--text)" }}>
            <b className="creamt">{BUILDING_NAMES[job.item.key] ?? job.item.name} · Level {job.item.level}</b> — <Countdown endMs={job.endMs} /> left.
            {used ? " This job has already been sped up (one speed-up per job)." : " One speed-up per job; the time comes off the end."}
          </p>
          <div className="rows">
            {speedups.map((s) => {
              const n = owned(v, s.key);
              const left = job.endMs - Date.now();
              return (
                <Row key={s.key}>
                  <Medallion src={art.store[s.key]} alt="" size={54} />
                  <RowText line={<>{s.label}</>} meta={n ? `${fmtNum(n)} owned${left > 0 && s.seconds * 1000 > left ? " · finishes it" : ""}` : "none owned"} />
                  <div className="right">
                    <Button
                      kind="gold"
                      size="md"
                      disabled={!n || used}
                      busy={actions.isBusy(`speedup:${s.key}`)}
                      onClick={async () => {
                        const r = await actions.run(`speedup:${s.key}`, () => api.village.speedupConstruction({ wid, vid }, s.key, job.index), {
                          success: `${fmtDuration(Math.min(left, s.seconds * 1000), { compact: true })} saved.`,
                        });
                        if (r) onClose();
                      }}
                    >
                      Use
                    </Button>
                  </div>
                </Row>
              );
            })}
          </div>
          {!speedups.some((s) => owned(v, s.key)) && <Empty>You own no speed-ups. They are sold in the Store under Speed Ups.</Empty>}
          <div style={{ marginTop: 16 }}>
            <Button kind="steel" size="md" wide onClick={() => { onClose(); nav(`${base}/store/speed_ups`); }}>Buy speed-ups in the Store</Button>
          </div>
        </>
      )}
    </Sheet>
  );
}
